/**
 * SottovoWordmark — inline SVG React component
 *
 * Renders the Sottovo wordmark with:
 *   - 'tt' ligature crossbar overlay (brand fingerprint)
 *   - Terminal 'o' Quiet Indigo hint ring (signals closed word)
 *   - Upright, Inter 500, wide tracking
 *
 * Props:
 *   size    — height in px (width scales proportionally via aspect ratio 340:68)
 *   color   — ink color for the wordmark text (default: Sotto Ink #1B1F2A)
 *   variant — 'wordmark' | 'lockup'  (.ai suffix shown in lockup mode)
 *
 * NOTE: Production use requires custom-glyph type design by a type designer.
 * The 'tt' crossbar and terminal 'o' accent are SVG overlay approximations
 * calibrated for Inter 500 at the default render size. They may need adjustment
 * if the base font changes.
 */

import { memo } from 'react';

interface SottovoWordmarkProps {
  /** Height in pixels. Width is computed from aspect ratio. */
  size?: number;
  /** Main wordmark ink color. Defaults to Sotto Ink. */
  color?: string;
  /** 'wordmark' = "Sottovo" only. 'lockup' = "Sottovo.ai" */
  variant?: 'wordmark' | 'lockup';
  /** Additional className for the SVG element */
  className?: string;
}

const SOTTO_INK = '#1B1F2A';
const QUIET_INDIGO = '#4F46E5';
const SLATE_MUTED = '#595959';

/**
 * Aspect ratio constants for the lockup viewBox (400 × 68).
 * Wordmark-only uses 340 × 68.
 */
const WORDMARK_ASPECT = 340 / 68;
const LOCKUP_ASPECT = 400 / 68;

export const SottovoWordmark = memo(function SottovoWordmark({
  size = 40,
  color = SOTTO_INK,
  variant = 'wordmark',
  className,
}: SottovoWordmarkProps) {
  const isLockup = variant === 'lockup';
  const aspect = isLockup ? LOCKUP_ASPECT : WORDMARK_ASPECT;
  const width = Math.round(size * aspect);
  const viewBox = isLockup ? '0 0 400 68' : '0 0 340 68';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={width}
      height={size}
      role="img"
      aria-label={isLockup ? 'Sottovo.ai' : 'Sottovo'}
      className={className}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* ── Wordmark text ── */}
      <text
        x="12"
        y="48"
        fontFamily="Inter, 'IBM Plex Sans', system-ui, sans-serif"
        fontWeight="500"
        fontSize="40"
        fill={color}
        letterSpacing="2"
        fontStyle="normal"
        textRendering="geometricPrecision"
      >
        Sottovo
      </text>

      {/*
        ── 'tt' ligature crossbar overlay ──
        A single shared crossbar spans both 't' glyphs, serving as the brand fingerprint.
        X range and Y position are calibrated for Inter 500 at 40px + letterSpacing=2.
        Adjust if the base font is changed.
      */}
      <rect
        x="61"
        y="29.5"
        width="34"
        height="2.8"
        rx="1.4"
        fill={color}
      />

      {/*
        ── Terminal 'o' closure accent ──
        A faint Quiet Indigo ring behind the final 'o' signals "the word closes here",
        contrasting with the open -ce ending of sottovoce.
      */}
      <circle
        cx="168"
        cy="30"
        r="14"
        fill="none"
        stroke={QUIET_INDIGO}
        strokeWidth="1.8"
        opacity={0.14}
      />

      {/* ── .ai suffix (lockup variant only) ── */}
      {isLockup && (
        <text
          x="197"
          y="48"
          fontFamily="Inter, 'IBM Plex Sans', system-ui, sans-serif"
          fontWeight="400"
          fontSize="40"
          fill={SLATE_MUTED}
          letterSpacing="1"
          fontStyle="normal"
          textRendering="geometricPrecision"
          opacity={0.75}
        >
          .ai
        </text>
      )}
    </svg>
  );
});

SottovoWordmark.displayName = 'SottovoWordmark';
